import {
  useStripe,
  useElements,
  CardExpiryElement,
  CardNumberElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import CardNumberIcon from '../../assets/Card 1.svg';
import CardExpiryIcon from '../../assets/Vector.svg';
import CardCVVIcon from '../../assets/Lock 1.svg';
import CardHolderNameInput from '../Inputs/CardHolderNameInput';
import InputAdornment from '@mui/material/InputAdornment';
import {
  FloatingLabelInput,
  formatName,
} from '../../modules/Consumer/Payment/Payment';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { ChangeEvent, useState } from 'react';
import ApiStatus from '../../config/apiStatusConstants';
import StyledButton from '../Button/Button';
import StyledBgContainer from '../StyledBgContainer/StyledBgContainer';
import { PaymentMethod } from '@stripe/stripe-js';
import { addCardDetails } from '../../services/NetworkServices';
import Popup from '../Inputs/Popup';
import { useNavigate } from 'react-router-dom';

interface CardModalProps {
  handleContinue: any;
}

const CardModal = ({ handleContinue }: CardModalProps) => {
  const [cardNumberComplete, setCardNumberComplete] = useState<boolean>(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState<boolean>(false);
  const [cardCvcComplete, setCardCvcComplete] = useState<boolean>(false);

  const [cardNumberFocused, setCardNumberFocused] = useState<boolean>(false);
  const [cardExpiryFocused, setCardExpiryFocused] = useState<boolean>(false);
  const [cardCvcFocused, setCardCvcFocused] = useState<boolean>(false);
  const [cardNumberHasInput, setCardNumberHasInput] = useState<boolean>(false);
  const [cardExpiryHasInput, setCardExpiryHasInput] = useState<boolean>(false);
  const [cardCvcHasInput, setCardCvcHasInput] = useState<boolean>(false);
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [successModal, setSuccessModal] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const navigate = useNavigate();

  const [paymentMethodDetails, setPaymentMethodDetails] =
    useState<PaymentMethod | null>(null);

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setCardHolderName(event.target.value);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setApiStatus(ApiStatus.LOADING);
    console.log('submit function');
    if (!stripe || !elements) {
      console.log("Stripe hasn't loaded yet. Please try again.");
      setErrorMsg("Stripe hasn't loaded yet. Please try again.");
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      return;
    }

    const { error: paymentError, paymentMethod } =
      await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardHolderName,
        },
      });

    if (paymentMethod) {
      setPaymentMethodDetails(paymentMethod);
    }

    if (paymentError) {
      setErrorMsg(paymentError.message || 'An error occurred');
      return;
    }

    const paymentMethodId = paymentMethod?.id;
    try {
      const response = await addCardDetails({ paymentMethodId });
      const data = await response.data;
      if (data.status) {
        setApiStatus(ApiStatus.SUCCESS);
        setSuccessModal(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(error.response?.data?.error?.raw?.message);
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton sx={{ mb: '5vh' }}>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          disabled={
            !cardNumberComplete ||
            !cardExpiryComplete ||
            !cardCvcComplete ||
            !cardHolderName
          }
          type="submit"
          sx={{ mb: '5vh', mt: '2vh' }}
          onClick={handleSubmit}
        >
          Save
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    console.log('error is', errorMsg);
    if (apiStatus === ApiStatus.FAILURE) {
      return <p style={{ color: '#FF1515' }}>{errorMsg}</p>;
    }
    return null;
  };

  return (
    // <Dialog
    //   onClose={onClose}
    //   aria-labelledby="customized-dialog-title"
    //   open={open}
    //   sx={{ overflowY: 'hidden' }}
    // >
    //   <DialogContent sx={{ padding: 0, height: '100vh', overflowY: 'none' }}>
    <Box
      sx={{
        height: '100%', // Match the height of DialogContent
        width: '100%', // Match the width of DialogContent
        overflow: 'hidden', // Prevent overflow
      }}
    >
      <>
        <Box
          component="form"
          sx={{
            width: { xs: '80vw', md: '47vw' },
            padding: { xs: '20px', md: '50px' },
            // Other styles...
          }}
        >
          <CardHolderNameInput
            type="text"
            onChange={onChangeName}
            value={formatName(cardHolderName)}
            label="Card Holder’s Name"
          />

          <FloatingLabelInput
            label="Card Number"
            focused={cardNumberFocused}
            hasValue={cardNumberComplete}
            hasInput={cardNumberHasInput}
          >
            <Box className="card">
              <InputAdornment position="start">
                <img
                  src={CardNumberIcon}
                  alt="Card Icon"
                  className="card-icon"
                />
              </InputAdornment>
              <CardNumberElement
                options={{
                  placeholder: '',
                  style: {
                    base: {
                      color: 'white',
                      fontSize: '16px',
                    },
                  },
                }}
                className="card-number"
                onFocus={() => setCardNumberFocused(true)}
                onBlur={() => setCardNumberFocused(false)}
                onChange={(event) => {
                  setCardNumberComplete(event.complete);
                  setCardNumberHasInput(!event.empty);
                }}
              />
            </Box>
          </FloatingLabelInput>

          <Box
            sx={{
              display: 'flex',
              gap: '15px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <FloatingLabelInput
              label="Expiry"
              focused={cardExpiryFocused}
              hasValue={cardExpiryComplete}
              hasInput={cardExpiryHasInput}
            >
              <Box className="card1">
                <InputAdornment position="start">
                  <img
                    src={CardExpiryIcon}
                    alt="Expiry Icon"
                    className="expiry-icon"
                  />
                </InputAdornment>
                <CardExpiryElement
                  options={{
                    placeholder: '',
                    style: {
                      base: {
                        color: 'white',
                        fontSize: '16px',
                      },
                    },
                  }}
                  className="card-number"
                  onFocus={() => setCardExpiryFocused(true)}
                  onBlur={() => setCardExpiryFocused(false)}
                  onChange={(event) => {
                    setCardExpiryComplete(event.complete);
                    setCardExpiryHasInput(!event.empty);
                  }}
                />
              </Box>
            </FloatingLabelInput>
            <FloatingLabelInput
              label="CVV"
              focused={cardCvcFocused}
              hasValue={cardCvcComplete}
              hasInput={cardCvcHasInput}
            >
              <Box className="card1">
                <InputAdornment position="start">
                  <img
                    src={CardCVVIcon}
                    alt="CVV Icon"
                    className="cvv-icon"
                    style={{
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      transform: 'translateY(-9px)',
                    }}
                  />
                </InputAdornment>
                <CardCvcElement
                  options={{
                    placeholder: '',
                    style: {
                      base: {
                        color: 'white',
                        fontSize: '16px',
                      },
                    },
                  }}
                  className="card-number"
                  onFocus={() => setCardCvcFocused(true)}
                  onBlur={() => setCardCvcFocused(false)}
                  onChange={(event) => {
                    setCardCvcComplete(event.complete);
                    setCardCvcHasInput(!event.empty);
                  }}
                />
              </Box>
            </FloatingLabelInput>
          </Box>
          {renderErrorMsg()}
          {renderButton()}
        </Box>
        {successModal && (
          <Popup
            title=<Typography
              variant="h6"
              sx={{
                fontFamily: 'opensans',
                fontWeight: '700',
                lineHeight: '1.4',
              }}
            >
              {' '}
              Your Card has been
              <br />
              updated Successfully{' '}
            </Typography>
            // message=<Typography variant="body2">
            //   {' '}
            //   This payment will appear on your credit card statement from <br />{' '}
            //   xxxx xxxx xxxx {paymentMethodDetails?.card?.last4 || ''}.
            // </Typography>
            buttonText="Done"
            onButtonClick={() => handleContinue()}
          />
        )}
      </>
    </Box>
    //   </DialogContent>
    // </Dialog>
  );
};

export default CardModal;
